<template>
  <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 py-8 md:py-10 px-4 xl:px-0 items-start">

    <!-- LEFT CONTENT -->
    <div class="order-1 md:col-span-8">

      <!-- Loading -->
      <div v-if="loading" class="card p-12 flex flex-col items-center gap-3 text-gray-500">
        <div class="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-brand-red"></div>
        Loading your artist profile...
      </div>

      <!-- Not Logged In -->
      <div v-else-if="!isLoggedIn" class="card p-10 md:p-12 text-center space-y-4">
        <div class="mx-auto w-14 h-14 rounded-md bg-red-50 text-brand-red flex items-center justify-center text-xl">
          <i class="fa-solid fa-lock"></i>
        </div>
        <h2 class="font-serif text-2xl font-bold text-brand-red">Please log in first</h2>
        <p class="text-gray-600">You need to be logged in to create an artist profile.</p>
        <router-link
          to="/login"
          class="btn-primary px-7"
        >
          Login
        </router-link>
      </div>

      <!-- Logged In but No Artist -->
      <div v-else-if="!hasArtist && !showArtistForm"
        class="card p-10 md:p-12 text-center space-y-4">
        <div class="mx-auto w-14 h-14 rounded-md bg-brand-cream/60 text-brand-red-dark flex items-center justify-center text-xl">
          <i class="fa-solid fa-palette"></i>
        </div>
        <h2 class="font-serif text-2xl font-bold text-brand-red">
          You have not posted anything yet
        </h2>
        <p class="text-gray-600">
          Please create your artist profile first.
        </p>
        <button
          @click="showArtistForm = true"
          class="btn-primary px-7"
        >
          Create Artist Profile
        </button>
      </div>

      <!-- INLINE ARTIST CREATE FORM -->
      <ArtistCreateForm
        v-else-if="showArtistForm"
        @created="onArtistCreated"
        @cancel="showArtistForm = false"
        @already-exists="onArtistAlreadyExists"
      />

      <!-- ARTIST PROFILE -->
      <div v-else class="card p-6 sm:p-8 md:p-10 h-auto space-y-10 gsap-artist-detail">

        <!-- Top Section -->
        <div class="flex flex-col md:flex-row md:items-center gap-6 md:gap-10 gsap-header">
          <div class="md:flex-1 space-y-2 text-left">
            <h1 class="font-serif text-3xl md:text-4xl font-bold text-brand-red leading-tight">{{ artist.name }}</h1>
            <h2 class="text-lg font-medium text-gray-500">{{ artist.profession }}</h2>
            <p v-if="artist.quote" class="!mt-4 border-l-4 border-brand-cream-dark pl-4 font-serif text-lg italic text-gray-700">"{{ artist.quote }}"</p>
            <p v-if="artist.dob" class="!mt-4 text-sm text-gray-500 flex items-center gap-2">
              <i class="fa-regular fa-calendar text-gray-400"></i>
              Born: {{ new Date(artist.dob).toLocaleDateString() }}
            </p>
          </div>

          <div class="md:w-[260px] flex-shrink-0">
            <img
              :src="artist.image"
              :alt="artist.name"
              class="w-full aspect-[4/3] md:aspect-square object-cover rounded-md shadow-md ring-4 ring-white bg-gray-100 gsap-image"
            />
          </div>
        </div>

        <!-- Description -->
        <div class="gsap-desc pt-8 border-t border-gray-100">
          <p class="prose-text">
            {{ artist.description }}
          </p>
        </div>

        <!-- Achievements -->
        <div v-reveal>
          <div class="section-heading mb-4">
            <h3 class="section-title">Achievements</h3>
          </div>
          <ul class="space-y-2.5 text-gray-700 leading-relaxed">
            <li v-for="(a, i) in formatAchievements(artist.achievements)" :key="i" class="flex gap-3">
              <i class="fa-solid fa-award text-brand-red mt-1.5 text-sm"></i>
              <span>{{ a }}</span>
            </li>
          </ul>
        </div>

        <!-- Gallery -->
        <section class="gsap-gallery">
          <div class="section-heading mb-4">
            <h2 class="section-title">Gallery</h2>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <img
              v-for="(img, index) in artist.galleryImages"
              :key="index"
              :src="img"
              class="w-full h-[220px] object-cover rounded-md shadow-sm bg-gray-100"
            />
          </div>
        </section>

        <!-- Social Links (Moved) -->
        <div v-if="artist.links && artist.links.length" v-reveal class="pt-8 border-t border-gray-100">
          <div class="section-heading mb-4">
            <h3 class="section-title">Connect</h3>
          </div>
          <div class="flex flex-wrap gap-3">
            <a
              v-for="(link, index) in artist.links"
              :key="index"
              :href="ensureUrl(link.link_url)"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center px-5 py-2 bg-white text-gray-700 rounded-md shadow-sm hover:shadow-md hover:border-brand-red hover:text-brand-red transition-all border border-gray-200 group"
            >
              <span class="font-medium">{{ link.link_type }}</span>
              <i class="fa-solid fa-arrow-up-right-from-square ml-2 text-xs text-brand-red"></i>
            </a>
          </div>
        </div>

      </div>
    </div>

    <!-- RIGHT SIDEBAR -->
    <div class="order-2 md:col-span-4">
      <RightSidebar />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import RightSidebar from "../components/RightsideBar.vue";
import ArtistCreateForm from "../components/ArtistCreateForm.vue";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const API_BASE_URL = "https://sanskritirdapoon.in/api";
const FILE_BASE_URL = "https://sanskritirdapoon.in";

// STATES
const loading = ref(true);
const hasArtist = ref(true);
const isLoggedIn = ref(false);
const showArtistForm = ref(false);

// ARTIST DATA
const artist = ref({
  name: "",
  profession: "",
  dob: "",
  quote: "",
  description: "",
  achievements: "",
  image: `${FILE_BASE_URL}/images/default.png`,
  galleryImages: [],
});

// ENSURE URL
function ensureUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
}

// FORMAT ACHIEVEMENTS
function formatAchievements(text) {
  if (!text) return [];
  return text.split(/[\n,]+/).map(a => a.trim()).filter(Boolean);
}

// FETCH ARTIST
async function fetchArtist() {
  loading.value = true;

  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  isLoggedIn.value = !!(user && token);

  if (!user?.id) {
    hasArtist.value = false;
    loading.value = false;
    return;
  }

  try {
    const res = await fetch(`${API_BASE_URL}/artists/by-user/${user.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status === 404) {
      hasArtist.value = false;
      return;
    }

    if (!res.ok) {
      hasArtist.value = false;
      return;
    }

    const data = await res.json();

    artist.value = {
      ...data,
      image: data.image
        ? (data.image.startsWith('data:') ? data.image : `${FILE_BASE_URL}${data.image}`)
        : `${FILE_BASE_URL}/images/default.png`,
      galleryImages: data.galleryImages?.map(i => `${FILE_BASE_URL}${i}`) || [],
    };

    hasArtist.value = true; // User has an artist profile
    await nextTick();
    animate();
  } catch (e) {
    hasArtist.value = false;
  } finally {
    loading.value = false;
  }
}

// AFTER CREATE
function onArtistCreated() {
  showArtistForm.value = false;
  fetchArtist();
}

// HANDLE ALREADY EXISTS
function onArtistAlreadyExists() {
  showArtistForm.value = false;
  fetchArtist(); // Refresh to show existing artist profile
}

// GSAP
let gsapCtx;
function animate() {
  // Revert only this page's previous animations (see ProfilePage.vue)
  if (gsapCtx) gsapCtx.revert();
  gsapCtx = gsap.context(() => {
    gsap.from(".gsap-artist-detail", { opacity: 0, y: 30, duration: 0.6 });
    gsap.from(".gsap-image", { opacity: 0, scale: 0.96, duration: 0.7 });
    gsap.from(".gsap-gallery img", {
      scrollTrigger: { trigger: ".gsap-gallery", start: "top 85%" },
      opacity: 0,
      y: 30,
      stagger: 0.1,
    });
  });
}

onBeforeUnmount(() => {
  if (gsapCtx) gsapCtx.revert();
});

onMounted(fetchArtist);
</script>
